import React from "react";
import classNames from "classnames";
import { Progress } from "@/components/shared/data-display/progress/progress";
import { formatUsdWithCommaSeparator } from "@/utility/string.utility";
import styles from "./free-shipping-progress.module.scss";

export interface FreeShippingProgressProps {
  /** The optional class applied to the root element */
  className?: string;

  /** The shipping hurdle to reach before free shipping */
  shippingHurdle?: number;

  /** The current subtotal in dollars */
  currentSubtotal?: number;
}

/**
 * Progress bar showing the user's progress toward the free shipping hurdle
 * @param props The component props
 * @param props.className The optional class applied to the root element
 * @param props.shippingHurdle The shipping hurdle in dollars
 * @param props.currentSubtotal The current subtotal in dollars
 * @returns Returns a component with a progress bar
 */
export const FreeShippingProgress = ({
  className,
  shippingHurdle = Number(process.env.NEXT_PUBLIC_FREE_SHIPPING_THRESHOLD),
  currentSubtotal = 0,
}: FreeShippingProgressProps) => {
  const progressValue = React.useMemo(() => {
    if (shippingHurdle === 0) {
      return 0;
    }

    const value = (100 * currentSubtotal) / shippingHurdle;

    return Math.max(0, Math.min(100, value));
  }, [currentSubtotal, shippingHurdle]);

  const message = React.useMemo(() => {
    if (currentSubtotal >= shippingHurdle) {
      return "You've unlocked FREE US Shipping!";
    }

    const remaining = Math.min(
      shippingHurdle,
      Math.max(0, shippingHurdle - currentSubtotal),
    );

    return `Add ${formatUsdWithCommaSeparator(remaining)} to get FREE U.S. Shipping`;
  }, [shippingHurdle, currentSubtotal]);

  return (
    <div className={classNames(styles.root, className)}>
      <div className={styles.message} data-testid="message">
        {message}
      </div>
      <div className={styles.progressContainer}>
        <Progress
          className={styles.progress}
          progressType="linear"
          variant="determinate"
          value={progressValue}
        />
        <div data-testid="hurdle">${shippingHurdle.toFixed(0)}</div>
      </div>
    </div>
  );
};
